<template>
  <bottom-sheet
    :visible="visible"
    :is-full-screen="isMobile"
    @close="$emit('close')"
  >
    <div class="max-h-[600px] px-2">
      <div class="mb-4">
        <h4 class="text-left text-2xl text-dark-blue">Filters</h4>
        <span
          class="absolute right-4 top-4 z-20 cursor-pointer"
          @click="handleResetFilters"
        >
          <p>Reset</p>
        </span>
      </div>

      <div class="mb-8 flex flex-col gap-5">
        <div class="flex items-end gap-2">
          <div class="flex-4 relative flex flex-col gap-2">
            <h4 class="text-left">Origin</h4>
            <div class="relative">
              <input
                v-model="filters.origin"
                type="text"
                class="h-9 w-full rounded-md border-light-gray bg-lighter-gray bg-opacity-30 pl-10 pr-3 text-sm text-gray-500 shadow-sm outline-none"
                placeholder="Search an Origin"
                @input="autocompleteInput('origin')"
              />

              <img
                class="absolute left-3 top-2"
                src="@/assets/origin-marker.svg"
                alt="origin icon"
              />

              <img
                v-if="filters.selectedOrigin"
                class="absolute right-10 top-3 z-30 h-3 w-3 cursor-pointer opacity-30"
                src="@/assets/close-icon.svg"
                alt="close icon"
                @click="clearOrigin"
              />

              <img
                class="absolute right-3 top-3.5 cursor-pointer"
                src="@/assets/arrow-down-gray.svg"
                alt="down icon"
              />
            </div>
            <div
              v-if="isOriginLoading"
              class="absolute top-[68px] z-50 w-full rounded-md bg-white py-2 shadow-lg"
            >
              <img
                class="loading m-auto"
                src="@/assets/loading-v2-yellow.svg"
                alt="loading spinner"
              />
            </div>

            <ul
              v-else-if="originResults?.length > 0"
              class="absolute top-[68px] z-50 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
              tabindex="-1"
            >
              <li
                v-for="(item, itemIndex) in originResults"
                :key="itemIndex"
                class="group relative cursor-pointer select-none py-2 pl-8 pr-4 hover:bg-yellow-500 hover:text-white"
                @click="handleAutocompleteResult('origin', item)"
              >
                <span class="flex h-6 items-center truncate font-medium">
                  {{ item.description }}
                </span>
              </li>
            </ul>
          </div>

          <div class="flex h-9 min-w-[90px] flex-1 items-center rounded">
            <dropdown-select
              :options="deadheadOptions"
              default="150 mi"
              :value="selectedOriginDeadhead"
              :exclude-selected="true"
              class="!h-9 border border-light-gray !bg-lighter-gray !bg-opacity-30 text-xs"
              @input="handleOriginDeadheadSelected"
            />
          </div>
        </div>

        <div class="flex items-end gap-2">
          <div class="relative flex flex-col gap-2">
            <h4 class="text-left">Destination</h4>
            <div class="relative">
              <input
                v-model="filters.destination"
                type="text"
                class="h-9 w-full rounded-md border-light-gray bg-lighter-gray bg-opacity-30 pl-10 pr-3 text-sm text-gray-500 shadow-sm outline-none"
                placeholder="Search a Destination"
                @input="autocompleteInput('destination')"
              />

              <img
                class="absolute left-3 top-[6px]"
                src="@/assets/loads/destination-marker.svg"
                alt="destination icon"
              />

              <img
                v-if="filters.selectedDestination"
                class="absolute right-10 top-3 z-30 h-3 w-3 cursor-pointer opacity-30"
                src="@/assets/close-icon.svg"
                alt="close icon"
                @click="clearDestination"
              />

              <img
                class="absolute right-3 top-3.5 cursor-pointer"
                src="@/assets/arrow-down-gray.svg"
                alt="down icon"
              />
            </div>
            <div
              v-if="isDestinationLoading"
              class="absolute top-[68px] z-50 w-full rounded-md bg-white py-2 shadow-lg"
            >
              <img
                class="loading m-auto"
                src="@/assets/loading-v2-yellow.svg"
                alt="loading spinner"
              />
            </div>

            <ul
              v-else-if="destinationResults?.length > 0"
              class="absolute top-[68px] z-50 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
              tabindex="-1"
            >
              <li
                v-for="(item, itemIndex) in destinationResults"
                :key="itemIndex"
                class="group relative cursor-pointer select-none py-2 pl-8 pr-4 hover:bg-yellow-500 hover:text-white"
                @click="handleAutocompleteResult('destination', item)"
              >
                <span class="flex h-6 items-center truncate font-medium">
                  {{ item.description }}
                </span>
              </li>
            </ul>
          </div>

          <div class="flex h-9 min-w-[90px] flex-1 items-center rounded">
            <dropdown-select
              :options="deadheadOptions"
              default="150 mi"
              :value="selectedDestinationDeadhead"
              :exclude-selected="true"
              class="!h-9 border border-light-gray !bg-lighter-gray !bg-opacity-30 text-xs"
              @input="handleDestinationDeadheadSelected"
            />
          </div>
        </div>

        <div class="relative py-1">
          <p
            class="mb-3 flex items-center justify-between bg-white text-dark-gray"
          >
            Broker Name
          </p>
          <input
            v-model="filters.searchQuery"
            type="text"
            class="h-12 w-full rounded-md border-light-gray bg-lighter-gray bg-opacity-30 pr-16 text-sm text-gray-500 shadow-sm outline-none"
            placeholder="Search Broker"
            maxLength="100"
          />
          <button
            type="submit"
            class="absolute bottom-5 right-0 flex w-8 flex-col justify-center bg-transparent text-placeholder-gray hover:text-yellow-500"
          >
            <img
              class="h-4 w-4 cursor-pointer"
              src="@/assets/search-light-gray.svg"
              alt="search icon"
            />
          </button>

          <img
            v-if="filters.searchQuery"
            class="absolute bottom-[22px] right-12 h-3 w-3 cursor-pointer opacity-30"
            src="@/assets/close-icon.svg"
            alt="close icon"
            @click="clearSearchInput"
          />
        </div>

        <div>
          <h4 class="mb-3 text-left text-xl">Dates</h4>

          <div class="mb-3 flex flex-col">
            <h4 class="text-left">Pickup Date Range</h4>
            <div class="relative w-full">
              <form-field-date
                label=" "
                :placeholder="!filters.pickup_start ? initialDate : ''"
                :model-value="pickupPlaceholder"
                name="pickup_date"
                input-type="date"
                :is-slim="true"
                :hidden="false"
                :monospace="false"
                :show-confirm-button="false"
                @update:model-value="dynamicUpdatePickupDate"
                @toggle-date-picker="toggleShowPickupDatePicker"
              />

              <img
                v-if="pickupPlaceholder"
                class="absolute right-14 top-[26px] z-30 h-3 w-3 cursor-pointer opacity-30"
                src="@/assets/close-icon.svg"
                alt="close icon"
                @click="clearPickup"
              />

              <div
                v-show="showPickupDatePicker"
                class="absolute right-0 top-full z-50 rounded-lg bg-white p-4 shadow-md"
              >
                <date-picker-range
                  :start-date="startPickupDate"
                  :end-date="endPickupDate"
                  :field="'pickup_datepicker'"
                  date-format="MM/DD/YYYY"
                  :disable-previous="true"
                  :restrict-previous="true"
                  @day-selected="setSelectedPickupDay"
                />
              </div>
            </div>
          </div>

          <div class="flex flex-col">
            <h4 class="text-left">Dropoff Date Range</h4>
            <div class="relative w-full">
              <form-field-date
                label=" "
                :placeholder="!filters.dropoff_start ? initialDatePlusTwo : ''"
                :model-value="dropoffPlaceholder"
                name="pickup_date"
                input-type="date"
                :is-slim="true"
                :hidden="false"
                :monospace="false"
                :show-confirm-button="false"
                @update:model-value="dynamicUpdateDropoffDate"
                @toggle-date-picker="toggleShowDropoffDatePicker"
              />

              <img
                v-if="dropoffPlaceholder"
                class="absolute right-14 top-[26px] z-30 h-3 w-3 cursor-pointer opacity-30"
                src="@/assets/close-icon.svg"
                alt="close icon"
                @click="clearDropoff"
              />

              <div
                v-show="showDropoffDatePicker"
                class="absolute right-0 top-full z-50 rounded-lg bg-white p-4 shadow-md"
              >
                <date-picker-range
                  :start-date="startDropoffDate"
                  :end-date="endDropoffDate"
                  :field="'dropoff_datepicker'"
                  date-format="MM/DD/YYYY"
                  :disable-previous="true"
                  :restrict-previous="true"
                  :restrict-previous-date="filters.pickup_end"
                  @day-selected="setSelectedDropoffDay"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="flex flex-col gap-2">
          <h4 class="text-left">Equipment Types</h4>
          <div class="checkbox-container grid grid-cols-2 gap-2">
            <div
              v-for="[key, val] in Object.entries(equipment_types)"
              :key="key"
              class="col-span-1"
            >
              <Checkbox
                :item="val"
                :active="filters.equipment?.includes(key)"
                :handle-select="(value) => handleSelectEquipment(key, value)"
              />
            </div>
          </div>
        </div>

        <div class="relative flex flex-col gap-2">
          <h4 class="text-left text-1lg">Minimum Rate</h4>

          <input
            v-model="filters.min_rate"
            class="h-9 w-full rounded-md border-light-gray bg-lighter-gray bg-opacity-30 px-3 text-sm text-gray-500 shadow-sm outline-none"
            placeholder="Set Minimum Rate"
            type="tel"
            inputmode="numeric"
            pattern="[0-9]*"
          />

          <img
            v-if="filters.min_rate"
            class="absolute bottom-3 right-4 h-3 w-3 cursor-pointer opacity-30"
            src="@/assets/close-icon.svg"
            alt="close icon"
            @click="clearMinRate"
          />
        </div>

        <div class="flex flex-col gap-2">
          <h4 class="mb-3 text-left text-1lg">Brokers</h4>

          <div class="flex items-center justify-between">
            <p
              class="flex items-center justify-between bg-white text-dark-gray"
            >
              Connected Brokers Only
            </p>
            <toggle-icon
              :model-value="filters.status"
              @update:model-value="handleStatusFilter"
            ></toggle-icon>
          </div>
        </div>

        <!-- <div class="flex flex-col gap-2">
          <h4 class="text-left text-1lg">Trailer Length</h4>

          <input
            v-model="filters.trailer_length"
            class="h-9 w-full rounded-md border-light-gray bg-lighter-gray bg-opacity-30 px-3 text-sm text-gray-500 shadow-sm outline-none"
            placeholder="Set trailer length (ft)"
            type="tel"
            inputmode="numeric"
            pattern="[0-9]*"
          />
        </div> -->

        <div class="flex flex-col">
          <h4 class="text-left">Posting Age</h4>
          <div class="checkbox-container flex flex-col gap-2">
            <Checkbox
              item="All Postings"
              :active="filters.posting.includes('all')"
              :handle-select="(value) => handleSelectPosting('all', value)"
            />
            <div class="flex gap-2">
              <Checkbox
                item="<8 hours"
                :active="filters.posting.includes('8hr')"
                :handle-select="(value) => handleSelectPosting('8hr', value)"
              />
              <Checkbox
                item="8-12 hours"
                :active="filters.posting.includes('12hr')"
                :handle-select="(value) => handleSelectPosting('12hr', value)"
              />
            </div>
            <div class="flex gap-2">
              <Checkbox
                item="12-24 hours"
                :active="filters.posting.includes('24hr')"
                :handle-select="(value) => handleSelectPosting('24hr', value)"
              />
              <Checkbox
                item="24-72 hours"
                :active="filters.posting.includes('72hr')"
                :handle-select="(value) => handleSelectPosting('72hr', value)"
              />
            </div>
          </div>
        </div>

        <div class="flex flex-col">
          <h4 class="text-left">Type</h4>
          <div class="checkbox-container flex flex-col gap-2">
            <!-- <Checkbox
              item="None"
              :active="filters.certifications.includes('none')"
              :handle-select="
                (value) => handleSelectCertification('none', value)
              "
            /> -->
            <Checkbox
              item="Hazmat"
              :active="filters.hazmat"
              :handle-select="(value) => handleTypeSelection('hazmat', value)"
            />
            <Checkbox
              item="Teams"
              :active="filters.teams"
              :handle-select="(value) => handleTypeSelection('teams', value)"
            />
          </div>
        </div>

        <!-- <div class="flex flex-col gap-2">
          <div class="flex items-center justify-between">
            <p
              class="flex items-center justify-between rounded-lg bg-white text-dark-gray"
            >
              Team Driver
            </p>
            <toggle-icon
              :model-value="filters.team"
              @update:model-value="handleTeamFilter"
            ></toggle-icon>
          </div>
        </div> -->

        <div class="relative flex flex-col gap-2">
          <h4 class="text-left text-1lg">Weight</h4>

          <input
            v-model="filters.weight"
            class="h-9 w-full rounded-md border-light-gray bg-lighter-gray bg-opacity-30 px-3 text-sm text-gray-500 shadow-sm outline-none"
            placeholder="Set weight"
            type="tel"
            inputmode="numeric"
            pattern="[0-9]*"
          />

          <img
            v-if="filters.weight"
            class="absolute bottom-3 right-4 h-3 w-3 cursor-pointer opacity-30"
            src="@/assets/close-icon.svg"
            alt="close icon"
            @click="clearWeight"
          />
        </div>
      </div>

      <div class="pb-8">
        <ButtonComponent
          variant="primary"
          class="flex w-full justify-center py-4"
          :disabled="emptyFilters"
          @click="() => handleApplyFilter()"
        >
          Apply
        </ButtonComponent>
      </div>
    </div>
  </bottom-sheet>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import dayjs from 'dayjs';
import debounce from 'lodash.debounce';
import {
  BottomSheet,
  Checkbox,
  FormFieldDate,
  DatePickerRange,
  ButtonComponent,
  SingleToggleIcon as ToggleIcon,
} from '@/components';
import { LoadDropdown as DropdownSelect } from '@/components/loads';
import { useScreenSize } from '@/composables';
import { createFilterQuery } from '@/utils';

const props = defineProps({
  visible: Boolean,
});

const emit = defineEmits(['close']);

const deadheadOptions = [
  '50 mi',
  '100 mi',
  '150 mi',
  '200 mi',
  '250 mi',
  '300 mi',
  '350 mi',
  '400 mi',
  '450 mi',
  '500 mi',
];

const store = useStore();
const router = useRouter();
const { screenSize } = useScreenSize();

const isMobile = computed(() => screenSize.value === 'mobile');

const initialDate = computed(() => {
  return dayjs().format('MM/DD/YYYY');
});

const initialDatePlusTwo = computed(() => {
  return dayjs().add(2, 'day').format('MM/DD/YYYY');
});

// const initialDatePickupDate = computed(() => {
//   return router.currentRoute.value.query?.pickup_start
//     ? dayjs(router.currentRoute.value.query?.pickup_start).format('MM/DD/YYYY')
//     : dayjs().format('MM/DD/YYYY');
// });
// const initialDateDropoffDate = computed(() => {
//   return router.currentRoute.value.query?.dropoff_start
//     ? dayjs(router.currentRoute.value.query?.dropoff_start).format('MM/DD/YYYY')
//     : dayjs().add(2, 'day').format('MM/DD/YYYY');
// });

const defaultFilters = {
  equipment: [],
  origin: '',
  destination: '',
  posting: [],
  rate: [],
  certifications: [],
  min_rate: '',
  deadhead: '',
  origin_deadhead: '',
  destination_deadhead: '',
  origin_area_id: '',
  destination_area_id: '',
  weight: '',
  team: false,
  teams: false,
  hazmat: false,
  status: false,
  searchQuery: '',
  trailer_length: '',
};

const loadSearchFilters = computed(() => ({
  ...defaultFilters,
  ...store.state.loads.loadSearchFilters,
}));

const filters = ref(loadSearchFilters?.value);
const showPickupDatePicker = ref(false);
const showDropoffDatePicker = ref(false);

const pickupPlaceholder = computed(() => {
  if (filters.value.pickup_start && filters.value.pickup_end) {
    return `${dayjs(filters.value.pickup_start).format('MMM DD, YYYY')} - ${dayjs(filters.value.pickup_end).format('MMM DD, YYYY')}`;
  } else {
    return '';
  }
});
const dropoffPlaceholder = computed(() => {
  if (filters.value.dropoff_start && filters.value.dropoff_end) {
    return `${dayjs(filters.value.dropoff_start).format('MMM DD, YYYY')} - ${dayjs(filters.value.dropoff_end).format('MMM DD, YYYY')}`;
  } else {
    return '';
  }
});

watch(
  () => router.currentRoute.value?.query?.view,
  (newValue) => {
    if (newValue === 'dispatch') {
      filters.value = store.state.loads?.dispatch_filters;
    } else if (newValue === 'transit') {
      filters.value = store.state.loads?.transit_filters;
    } else {
      filters.value = store.state.loads?.completed_filters;
    }
  }
);

watch(
  () => props?.visible,
  (newValue) => {
    if (newValue) {
      if (Object.entries(store.state.loads?.loadSearchFilters)?.length > 0) {
        Object.entries(store.state.loads?.loadSearchFilters).forEach(
          ([key, value]) => {
            if (filters.value?.[key] !== value) {
              filters.value[key] = value;
            }
          }
        );
      }
    }
  }
);

const selectedPickupDate = ref(filters.value.pickup_start || '');
const selectedPickupEndDate = ref(filters.value.pickup_end || '');
const selectedDropoffDate = ref(filters.value.dropoff_start || '');
const selectedDropoffEndDate = ref(filters.value.dropoff_end || '');
const selectedOriginDeadhead = ref(
  filters.value.origin_deadhead
    ? `${filters.value?.origin_deadhead}${filters.value?.origin_deadhead.includes('mi') ? '' : ' mi'}`
    : '150 mi'
);

const selectedDestinationDeadhead = ref(
  filters.value.destination_deadhead
    ? `${filters.value?.destination_deadhead}${filters.value?.destination_deadhead.includes('mi') ? '' : ' mi'}`
    : '150 mi'
);

const equipment_types = computed(() => store.state.loads.equipment_types);

onMounted(async () => {
  await store.dispatch('loads/getEquipmentTypes');

  const pickupParams = router.currentRoute.value.query?.pickup;
  const dropoffParams = router.currentRoute.value.query?.dropoff;

  filters.value = defaultFilters;

  if (pickupParams) {
    selectedPickupDate.value = dayjs(pickupParams).format('MM/DD/YYYY');
  }
  if (dropoffParams) {
    selectedDropoffDate.value = dayjs(dropoffParams).format('MM/DD/YYYY');
  }

  const view = router.currentRoute.value.query?.view;

  if (view === 'dispatch') {
    store.commit('loads/setDispatchFilters', filters.value);
  } else if (view === 'transit') {
    store.commit('loads/setTransitFilters', filters.value);
  } else {
    store.commit('loads/setCompletedFilters', filters.value);
  }
});

const startPickupDate = computed(() => {
  return selectedPickupDate.value
    ? dayjs(selectedPickupDate.value).format('YYYY-MM-DD')
    : undefined;
});
const endPickupDate = computed(() => {
  return selectedPickupEndDate.value
    ? dayjs(selectedPickupEndDate.value).format('YYYY-MM-DD')
    : undefined;
});

const startDropoffDate = computed(() => {
  return selectedDropoffDate.value
    ? dayjs(selectedDropoffDate.value).format('YYYY-MM-DD')
    : undefined;
});
const endDropoffDate = computed(() => {
  return selectedDropoffEndDate.value
    ? dayjs(selectedDropoffEndDate.value).format('YYYY-MM-DD')
    : undefined;
});

function dynamicUpdatePickupDate({ value }) {
  if (value) {
    filters.value.pickup_start = selectedPickupDate.value;
    filters.value.pickup_end = selectedPickupEndDate.value;
  }
}

const setSelectedPickupDay = (fieldValue, dateSelected) => {
  if (
    !selectedPickupDate.value ||
    (selectedPickupDate.value && selectedPickupEndDate.value)
  ) {
    // set pickup start range
    selectedPickupDate.value = dayjs(dateSelected).format('YYYY-MM-DD');
    selectedPickupEndDate.value = undefined;
  } else {
    // set pickup end range
    selectedPickupEndDate.value = dayjs(dateSelected).format('YYYY-MM-DD');
    dynamicUpdatePickupDate({ name: fieldValue, value: dateSelected });
    showPickupDatePicker.value = false;
  }
};

watch(
  () => filters.value?.pickup_end,
  (newValue) => {
    if (newValue && filters.value.dropoff_end) {
      const pickupEnd = dayjs(newValue);
      const dropoffStart = dayjs(filters.value.dropoff_start);
      const isDropoffBeforePickup = dropoffStart.isBefore(pickupEnd);

      if (isDropoffBeforePickup) {
        console.log(
          'Pickup date has been changed to be before dropoff date. Clearing dropoff'
        );
        clearDropoff();
      }
    }
  }
);

const clearPickup = () => {
  filters.value.pickup_start = '';
  filters.value.pickup_end = '';
  selectedPickupDate.value = '';
  selectedPickupEndDate.value = '';
};

const clearDropoff = () => {
  filters.value.dropoff_start = '';
  filters.value.dropoff_end = '';
  selectedDropoffDate.value = '';
  selectedDropoffEndDate.value = '';
};

const clearMinRate = () => {
  filters.value.min_rate = '';
};

const clearWeight = () => {
  filters.value.weight = '';
};

const toggleShowPickupDatePicker = () => {
  showPickupDatePicker.value = !showPickupDatePicker.value;
};

function dynamicUpdateDropoffDate({ value }) {
  if (value) {
    filters.value.dropoff_start = selectedDropoffDate.value;
    filters.value.dropoff_end = selectedDropoffEndDate.value;
  }
}

const setSelectedDropoffDay = (fieldValue, dateSelected) => {
  if (
    !selectedDropoffDate.value ||
    (selectedDropoffDate.value && selectedDropoffEndDate.value)
  ) {
    // set dropoff start range
    selectedDropoffDate.value = dayjs(dateSelected).format('YYYY-MM-DD');
    selectedDropoffEndDate.value = undefined;
  } else {
    // set dropoff end range
    selectedDropoffEndDate.value = dayjs(dateSelected).format('YYYY-MM-DD');
    dynamicUpdateDropoffDate({ name: fieldValue, value: dateSelected });
    showDropoffDatePicker.value = false;
  }
};

const toggleShowDropoffDatePicker = () => {
  showDropoffDatePicker.value = !showDropoffDatePicker.value;
};

const clearOrigin = () => {
  filters.value.origin = '';
  filters.value.origin_area_id = '';
  filters.value.selectedOrigin = '';
};

const clearDestination = () => {
  filters.value.destination = '';
  filters.value.destination_area_id = '';
  filters.value.selectedDestination = '';
};

const handleSelectEquipment = (value) => {
  if (filters.value.equipment?.includes(value)) {
    filters.value.equipment = [];
  } else {
    filters.value.equipment = [value];
  }
};

const handleSelectPosting = (value) => {
  if (filters.value.posting?.includes(value)) {
    filters.value.posting = [];
  } else {
    filters.value.posting = [value];
  }
};

const handleTypeSelection = (type) => {
  if (filters.value?.[type] === true) {
    filters.value[type] = false;
  } else {
    filters.value[type] = true;
  }
};

const handleStatusFilter = (val) => {
  filters.value.status = val;
};

const clearSearchInput = () => {
  filters.value.searchQuery = '';
};

const isOriginLoading = ref(false);
const isDestinationLoading = ref(false);

const originResults = computed(
  () => store.state.laneSearches?.origin?.autocompleteResults
);
const destinationResults = computed(
  () => store.state.laneSearches?.destination?.autocompleteResults
);

const emptyFilters = computed(() => {
  return (
    !filters.value?.origin_area_id &&
    !filters.value?.destination_area_id &&
    !filters.value?.searchQuery
  );
});

const autocompleteInput = debounce(async (type) => {
  if (filters.value?.[type]) {
    if (type === 'origin') {
      isOriginLoading.value = true;
    } else {
      isDestinationLoading.value = true;
    }
    try {
      await store.dispatch('laneSearches/getAutocomplete', {
        isType: type,
        term: filters.value?.[type],
      });
    } catch (error) {
      console.error('autocomplete error: ', error);
    } finally {
      isOriginLoading.value = false;
      isDestinationLoading.value = false;
    }
  }
}, 500);

// TODO -- handle blur of autocomplete (click outside)
const handleAutocompleteResult = async (type, item) => {
  await store.dispatch('laneSearches/updateFromGooglePlace', {
    isType: type,
    googlePlace: item,
  });
  switch (type) {
    case 'origin':
      store.commit('laneSearches/updateOrigin', {
        autocompleteResults: [],
        showBadge: true,
      });
      filters.value.origin = item?.description;
      break;
    case 'destination':
      store.commit('laneSearches/updateDestination', {
        autocompleteResults: [],
        showBadge: true,
      });
      filters.value.destination = item?.description;
      break;
  }

  const areaInformation = await store.dispatch(
    'laneSearches/updateAreaIdFromGooglePlace',
    type
  );
  if (areaInformation?.data) {
    if (type === 'origin') {
      filters.value.origin_area_id = areaInformation?.data?.id;
      filters.value.selectedOrigin = areaInformation?.data?.code;
    } else {
      filters.value.destination_area_id = areaInformation?.data?.id;
      filters.value.selectedDestination = areaInformation?.data?.code;
    }
  }
};

const handleApplyFilter = async (clearAll) => {
  const filterParams = createFilterQuery(
    filters.value,
    router.currentRoute.value.query
  );
  router.push({
    query: clearAll ? {} : filterParams,
  });

  await store.commit('loads/setLoadSearchFilters', filters.value);

  const view = router.currentRoute.value?.query?.view;

  if (view === 'dispatch') {
    store.commit('loads/setDispatchFilters', filters.value);
  } else if (view === 'transit') {
    store.commit('loads/setTransitFilters', filters.value);
  } else {
    store.commit('loads/setCompletedFilters', filters.value);
  }

  emit('close');
};

const isFiltersEmpty = computed(() => {
  return !Object.values(filters.value).some((el) => {
    if (Array.isArray(el)) {
      return el?.length > 0;
    } else {
      return el;
    }
  });
});

async function handleResetFilters() {
  if (!isFiltersEmpty.value) {
    console.log('clear filters');
    const emptyFilters = {
      equipment: [],
      origin: '',
      destination: '',
      posting: [],
      rate: [],
      certifications: [],
      min_rate: '',
      deadhead: '',
      origin_deadhead: '',
      destination_deadhead: '',
      origin_area_id: '',
      destination_area_id: '',
      weight: '',
      team: false,
      status: false,
      searchQuery: '',
      trailer_length: '',
    };
    filters.value = emptyFilters;
    selectedPickupDate.value = initialDate.value;
    selectedDropoffDate.value = initialDate.value;

    selectedOriginDeadhead.value = '150 mi';
    selectedDestinationDeadhead.value = '150 mi';

    await store.commit('loads/setLoadSearchFilters', emptyFilters);

    await store.commit('laneSearches/setAutocompleteResults', {
      results: [],
      isType: 'origin',
    });
    await store.commit('laneSearches/setAutocompleteResults', {
      results: [],
      isType: 'destination',
    });

    handleApplyFilter(true);
  }

  emit('close');
}

const handleOriginDeadheadSelected = (item) => {
  const deadheadDistance = item?.split(' ')?.[0];
  console.log('Select origin deadhead', deadheadDistance);

  filters.value.origin_deadhead = deadheadDistance;
  selectedOriginDeadhead.value = `${deadheadDistance} Mi`;
};
const handleDestinationDeadheadSelected = (item) => {
  const deadheadDistance = item?.split(' ')?.[0];
  console.log('Select destination deadhead', deadheadDistance);

  filters.value.destination_deadhead = deadheadDistance;
  selectedDestinationDeadhead.value = `${deadheadDistance} Mi`;
};
</script>

<style>
.checkbox-container .container {
  max-width: unset;
  @apply border-l-4 border-transparent pl-9;
}
.checkbox-container .container > div {
  @apply text-sm;
}

.checkbox-container .checkbox-checkmark {
  @apply h-5 w-5;
}

.checkbox-container .container .checkbox-checkmark:after {
  border-color: black;
  top: 3px;
  left: 7px;
  width: 7px;
  height: 12px;
}

.checkbox-container .activeButtonText {
  @apply border-yellow-500 bg-gray-100;
}
</style>
